import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';

const CaseInfoCard = ({
    caseRec,
    clientRec,
    caseSlaAlert,
    upcomingAlerts,
    saveCase,
    savingCase,
    staff,
    taskStats
}) => {
    const [caseAssignQuery, setCaseAssignQuery] = useState('');
    const [caseAssignSelectedEmail, setCaseAssignSelectedEmail] = useState('');

    const staffByEmail = useMemo(() => {
        const map = new Map();
        (staff || []).forEach(s => { if (s?.email) map.set(s.email.toLowerCase(), s); });
        return map;
    }, [staff]);

    const filterStaff = (q) => {
        const term = (q || '').trim().toLowerCase();
        if (!term) return (staff || []).slice(0, 8);
        return (staff || []).filter(s => (
            (s.name || '').toLowerCase().includes(term) || (s.email || '').toLowerCase().includes(term)
        )).slice(0, 8);
    };

    const caseAssignSuggestions = useMemo(() => filterStaff(caseAssignQuery), [caseAssignQuery, staff]);

    const getInitials = (nameOrEmail) => {
        if (!nameOrEmail) return '?';
        const n = String(nameOrEmail);
        if (n.includes(' ')) {
            const parts = n.trim().split(/\s+/);
            return (parts[0][0] + (parts[1]?.[0] || '')).toUpperCase();
        }
        if (n.includes('@')) return n[0].toUpperCase();
        return n.slice(0, 2).toUpperCase();
    };

    const colorFor = (key) => {
        const colors = [
            'bg-pink-900/50 text-pink-300 border-pink-800',
            'bg-indigo-900/50 text-indigo-300 border-indigo-800',
            'bg-emerald-900/50 text-emerald-300 border-emerald-800',
            'bg-amber-900/50 text-amber-300 border-amber-800',
            'bg-cyan-900/50 text-cyan-300 border-cyan-800',
            'bg-fuchsia-900/50 text-fuchsia-300 border-fuchsia-800',
        ];
        let h = 0;
        for (let i = 0; i < String(key).length; i++) h = (h * 31 + String(key).charCodeAt(i)) >>> 0;
        return colors[h % colors.length];
    };

    return (
        <div className="bg-gray-800 border border-gray-700 rounded-lg shadow-lg overflow-hidden mb-6">
            <div className="bg-gray-900/50 p-4 border-b border-gray-700 flex justify-between items-center">
                <h3 className="text-lg font-bold text-blue-400 uppercase tracking-wider flex items-center gap-2">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V8a2 2 0 00-2-2h-5m-4 0V5a2 2 0 114 0v1m-4 0c0 .884-.356 1.646-.925 2.203a1.502 1.502 0 01-2.15 0A2.248 2.248 0 018 6m0 0c0 .884.356 1.646.925 2.203a1.502 1.502 0 002.15 0" /></svg>
                    {caseRec.title}
                </h3>
                {caseSlaAlert && (
                    <div className="px-3 py-1 rounded text-xs font-bold border bg-red-900/30 text-red-400 border-red-800 animate-pulse">
                        ⚠️ {caseSlaAlert}
                    </div>
                )}
            </div>

            <div className="p-6">
                {upcomingAlerts.length > 0 && (
                    <div className="space-y-2 mb-6">
                        {upcomingAlerts.map((a, i) => (
                            <div key={i} className={`p-3 rounded border-l-4 text-sm font-mono ${a.type === 'overdue' ? 'bg-red-900/10 border-red-500 text-red-400' : 'bg-yellow-900/10 border-yellow-500 text-yellow-400'}`}>
                                {a.text}
                            </div>
                        ))}
                    </div>
                )}

                <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-6">
                    <div>
                        <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Status</label>
                        <select
                            className="w-full bg-gray-900 border border-gray-600 rounded px-3 py-2 text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 font-mono text-sm"
                            value={caseRec.status}
                            onChange={(e) => saveCase({ status: e.target.value })}
                            disabled={savingCase}
                        >
                            {['open', 'in_progress', 'on_hold', 'completed'].map(s => <option key={s} value={s}>{s.toUpperCase()}</option>)}
                        </select>
                    </div>
                    <div>
                        <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Priority</label>
                        <select
                            className="w-full bg-gray-900 border border-gray-600 rounded px-3 py-2 text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 font-mono text-sm"
                            value={caseRec.priority}
                            onChange={(e) => saveCase({ priority: e.target.value })}
                            disabled={savingCase}
                        >
                            {['low', 'medium', 'high'].map(p => <option key={p} value={p}>{p.toUpperCase()}</option>)}
                        </select>
                    </div>
                    <div>
                        <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Due date</label>
                        <input
                            type="date"
                            className="w-full bg-gray-900 border border-gray-600 rounded px-3 py-2 text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 font-mono text-sm"
                            value={caseRec.due_date || ''}
                            onChange={(e) => saveCase({ due_date: e.target.value })}
                            disabled={savingCase}
                        />
                    </div>
                    <div>
                        <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Assigned to</label>
                        <div className="flex items-center gap-2">
                            <div className={`w-8 h-8 rounded flex items-center justify-center text-xs font-bold border ${colorFor(caseRec.assigned_to || 'U')}`} title={caseRec.assigned_to || ''}>
                                {getInitials(staffByEmail.get(String(caseRec.assigned_to || '').toLowerCase())?.name || caseRec.assigned_to || 'U')}
                            </div>
                            <div className="relative flex-1">
                                <input
                                    className="w-full bg-gray-900 border border-gray-600 rounded px-3 py-2 text-white placeholder-gray-600 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 font-mono text-sm"
                                    placeholder="SEARCH STAFF..."
                                    value={caseAssignQuery}
                                    onChange={(e) => { setCaseAssignQuery(e.target.value); setCaseAssignSelectedEmail(''); }}
                                />
                                {/* Suggestions */}
                                {caseAssignQuery && (
                                    <div className="absolute z-10 mt-1 w-full bg-gray-800 border border-gray-600 rounded shadow-lg max-h-40 overflow-auto">
                                        {caseAssignSuggestions.map(s => (
                                            <div key={s.id} className="px-3 py-2 text-xs hover:bg-gray-700 cursor-pointer border-b border-gray-700 last:border-0"
                                                onClick={() => { setCaseAssignQuery(s.email); setCaseAssignSelectedEmail(s.email); }}>
                                                <div className="font-bold text-white">{s.name || s.email}</div>
                                                {s.name && <div className="text-gray-500">{s.email}</div>}
                                            </div>
                                        ))}
                                        {caseAssignSuggestions.length === 0 && (
                                            <div className="px-3 py-2 text-xs text-gray-500">NO MATCHES</div>
                                        )}
                                    </div>
                                )}
                            </div>
                            <button
                                className="px-3 py-2 rounded text-xs font-bold bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed uppercase tracking-wider"
                                disabled={!caseAssignSelectedEmail || savingCase}
                                onClick={() => {
                                    saveCase({ assigned_to: caseAssignSelectedEmail });
                                    setCaseAssignQuery('');
                                    setCaseAssignSelectedEmail('');
                                }}
                            >
                                Assign
                            </button>
                        </div>
                    </div>
                </div>

                {/* Quick stats */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4 border-t border-gray-700 pt-4">
                    <div className="text-center p-2">
                        <div className="text-[10px] text-gray-500 uppercase tracking-widest mb-1">Total Tasks</div>
                        <div className="text-2xl font-bold text-white font-mono">{taskStats.total}</div>
                    </div>
                    <div className="text-center p-2 border-l border-gray-700">
                        <div className="text-[10px] text-gray-500 uppercase tracking-widest mb-1">Open</div>
                        <div className="text-2xl font-bold text-blue-400 font-mono">{taskStats.open}</div>
                    </div>
                    <div className="text-center p-2 border-l border-gray-700">
                        <div className="text-[10px] text-gray-500 uppercase tracking-widest mb-1">Done</div>
                        <div className="text-2xl font-bold text-green-400 font-mono">{taskStats.done}</div>
                    </div>
                    <div className="text-center p-2 border-l border-gray-700">
                        <div className="text-[10px] text-gray-500 uppercase tracking-widest mb-1">Overdue</div>
                        <div className={`text-2xl font-bold font-mono ${taskStats.overdue > 0 ? 'text-red-500' : 'text-gray-600'}`}>{taskStats.overdue}</div>
                    </div>
                </div>

                <div className="text-xs text-gray-500 mt-2 flex items-center gap-4 flex-wrap font-mono border-t border-gray-700 pt-4">
                    <span className="flex items-center gap-2">
                        <span className="w-2 h-2 rounded-full bg-blue-500"></span>
                        STAGE: <span className="text-gray-300">{caseRec.current_stage || 'N/A'}</span>
                    </span>
                    {caseRec?.client_id && (
                        <span className="flex items-center gap-2">
                            <span className="w-2 h-2 rounded-full bg-purple-500"></span>
                            CLIENT: <Link to={`/admin/clients/${caseRec.client_id}`} className="text-blue-400 hover:text-blue-300 hover:underline">
                                {clientRec ? (`${clientRec.first_name || ''} ${clientRec.last_name || ''}`.trim() || clientRec.primary_email || caseRec.client_id) : caseRec.client_id}
                            </Link>
                        </span>
                    )}
                </div>
            </div>
        </div>
    );
};

export default CaseInfoCard;
